/**
 * EDI アプリのテーブルが共通で使う機能セット。
 *
 * v9 は使用する機能を明示的に登録する方式で、`Table` / `Column` の型引数に登録内容が
 * 乗る（かつ型引数は不変）。EDI のテーブルは発注一覧・商品一覧の2つだけで必要な機能も
 * 同じなので、機能セットをここに1つだけ置き、共有コンポーネント（ColumnHeader /
 * FilterPanel）もこの型で受ける。これで登録漏れはすべて型エラーとして検出される。
 *
 * 内訳:
 * - rowSortingFeature      : ColumnHeader のソートトグル
 * - columnFilteringFeature : FilterPanel の絞り込み入力
 * - columnVisibilityFeature: FilterPanel の getVisibleLeafColumns、行描画の getVisibleCells
 * - columnSizingFeature    : ヘッダーの `header.getSize()` による列幅指定
 *
 * ソート・フィルタ・ページングはサーバサイドで行う(`manualSorting` / `manualFiltering`)ため、
 * 行モデル(`createSortedRowModel` 等)と関数レジストリ(`filterFns` / `sortFns`)は登録しない。
 * v9 は manual 指定時に行モデル自体を短絡するので、これらは呼ばれない。
 */
import type { Column, RowData } from '@tanstack/react-table';
import {
  columnFilteringFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  rowSortingFeature,
  tableFeatures,
} from '@tanstack/react-table';

export const ediTableFeatures = tableFeatures({
  columnFilteringFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  rowSortingFeature,
});

export type EdiTableFeatures = typeof ediTableFeatures;

/*
 * 共有コンポーネント（ColumnHeader）が受け取る列の型。
 * v9 は型引数が不変なので、列ごとに異なる TValue（`string` / `number` など）を
 * そのまま受けられない。TValue だけ緩めて吸収する。
 */
// biome-ignore lint/suspicious/noExplicitAny: 列ごとに異なる TValue を吸収するための意図的な any
type AnyValue = any;

export type EdiColumn<TData extends RowData> = Column<EdiTableFeatures, TData, AnyValue>;
